/**
 * E-Sign Enums
 *
 * Enums for e-signature submission and submitter statuses.
 * Centralizes inline type literals used across business portal,
 * consumer portal, and API gateway.
 */

import { z } from 'zod';

// ============================================================================
// ESIGN SUBMISSION STATUS
// ============================================================================

/**
 * Status of an e-sign submission/envelope
 */
export enum EsignSubmissionStatus {
  /** Envelope created but not yet sent/signed */
  PENDING = 'pending',
  /** All parties have signed */
  COMPLETED = 'completed',
  /** Signing deadline has passed */
  EXPIRED = 'expired',
  /** Envelope has been archived */
  ARCHIVED = 'archived',
}

/** Zod schema for EsignSubmissionStatus validation */
export const EsignSubmissionStatusSchema = z.nativeEnum(EsignSubmissionStatus);

/** Array of all e-sign submission statuses for iteration */
export const ESIGN_SUBMISSION_STATUSES = Object.values(EsignSubmissionStatus);

// ============================================================================
// ESIGN SUBMITTER STATUS
// ============================================================================

/**
 * Status of an individual submitter/signer within an envelope
 */
export enum EsignSubmitterStatus {
  /** Awaiting action */
  PENDING = 'pending',
  /** Envelope sent to submitter */
  SENT = 'sent',
  /** Submitter has opened the envelope */
  OPENED = 'opened',
  /** Submitter has completed signing */
  COMPLETED = 'completed',
}

/** Zod schema for EsignSubmitterStatus validation */
export const EsignSubmitterStatusSchema = z.nativeEnum(EsignSubmitterStatus);

/** Array of all e-sign submitter statuses for iteration */
export const ESIGN_SUBMITTER_STATUSES = Object.values(EsignSubmitterStatus);

// ============================================================================
// SIGNING ENVELOPE AGREEMENT TYPE (Issue #1531 v4 Phase 6)
// ============================================================================

/**
 * Discriminator for `SigningEnvelope.agreementType` — drives webhook dispatch
 * to the correct post-completion handler (term sheet status flip, partnership
 * ReferralAgreement.status flip, etc.). Replaces the legacy free-string
 * `agreement_type` column values.
 *
 * Values are UPPERCASE strings (matches Prisma enum convention; legacy lowercase
 * values like `referral_agreement` are migrated by Phase 6 data migration).
 *
 * Stub kinds (NDA, VENDOR_CONTRACT, DISCLOSURE, EMPLOYMENT) reserve registry
 * slots in the webhook dispatcher so future handlers can plug in without
 * causing unhandled-webhook errors.
 *
 * `OTHER` is the catchall for migrated envelopes whose category couldn't be
 * inferred at backfill time.
 */
export enum SigningEnvelopeAgreementType {
  /** Term sheet (loan-side) — the most common agreement type */
  TERM_SHEET = 'TERM_SHEET',
  /** Partnership / referral agreement (B2B) — Phase 6 partnership e-sign */
  REFERRAL_AGREEMENT = 'REFERRAL_AGREEMENT',
  /** Non-disclosure agreement — stub handler */
  NDA = 'NDA',
  /** Vendor contract — stub handler */
  VENDOR_CONTRACT = 'VENDOR_CONTRACT',
  /** Disclosure document — stub handler */
  DISCLOSURE = 'DISCLOSURE',
  /** Employment agreement — stub handler */
  EMPLOYMENT = 'EMPLOYMENT',
  /** Catchall for migrated envelopes whose category couldn't be inferred */
  OTHER = 'OTHER',
}

/** Zod schema for SigningEnvelopeAgreementType validation */
export const SigningEnvelopeAgreementTypeSchema = z.nativeEnum(SigningEnvelopeAgreementType);

/** Array of all signing envelope agreement types for iteration */
export const SIGNING_ENVELOPE_AGREEMENT_TYPES = Object.values(SigningEnvelopeAgreementType);

/**
 * Resolve a free-string `SigningEnvelope.agreementType` value to the typed
 * enum. Handles legacy lowercase values (`'referral_agreement'`,
 * `'term_sheet'`, etc.) AND the canonical UPPERCASE values. Falls back to
 * `OTHER` for unknown values.
 *
 * Used by the webhook dispatcher to translate the free-string column into
 * the typed enum during the deprecation window before the Phase 6 data
 * migration backfills all rows.
 */
export function resolveSigningEnvelopeAgreementType(value: string | null | undefined): SigningEnvelopeAgreementType {
  if (!value) {
    return SigningEnvelopeAgreementType.OTHER;
  }
  const upper = value.toUpperCase();
  // Direct enum-key match (handles UPPERCASE values + most legacy lowercase
  // because Object.values() returns the string values which match upper(value)
  // for our enum since values are UPPERCASE strings).
  const match = SIGNING_ENVELOPE_AGREEMENT_TYPES.find((t) => t === upper);
  return match ?? SigningEnvelopeAgreementType.OTHER;
}
